import { useMemo } from "react";

// Let's leverage the `getTrialBannerDaysHoursText` function from the
// `NewselaGlobalLayoutV2`'s `TrialBanner` component for now, since both
// banners should follow (almost, read more bellow) the same logic for the time
// left text. Ideally we will have a single banner component in the future, and
// then we can colocate files
import { useQuery } from "@tanstack/react-query";
import { getTrialBannerDaysHoursText } from "@/base/NewselaGlobalLayoutV2/NavigationBar/PremiumTrial/TrialBanner/TrialBanner.utils";
import { BannerVariant } from "@/components/TrialExtensionBanner";
import getTrialLicenseFarthestExpirationDate from "@/utils/getTrialLicenseFarthestExpirationDate";
import useIsAtHomePage from "@/view/utils/useIsAtHomePage";
import useUserFeaturesQuery from "@/users/v3/useUserFeaturesQuery";
import { licensesQueries } from "@/users/v3/licenses";

const useTrialExtensionBanner = () => {
  const isAtHomePage = useIsAtHomePage();
  const { data: licenses } = useQuery({
    ...licensesQueries.get(),
    enabled: isAtHomePage,
  });
  const {
    daysToExpiration: daysToTrialExpiration,
    hoursToExpiration: hoursToTrialExpiration,
  } = useMemo(
    () => getTrialLicenseFarthestExpirationDate(licenses ?? []),
    [licenses],
  );
  const { data: userFeatures } = useUserFeaturesQuery();

  const getVariant = (): BannerVariant => {
    if (userFeatures?.includes("has_active_premium_trial_campaign_extension")) {
      return "trial_extension";
    }
    if (userFeatures?.includes("has_active_premium_trial_campaign")) {
      return "trial";
    }
    return "post_trial";
  };

  return {
    loading: isAtHomePage && !licenses,
    show:
      Boolean(userFeatures?.includes("can_see_trial_extension_banner")) &&
      Boolean(licenses),
    variant: getVariant(),
    // For now this component won't show the hours left in the last day of trial,
    // so let's force the last day to show "1 day"
    timeLeft:
      daysToTrialExpiration === 0
        ? "1 day"
        : getTrialBannerDaysHoursText({
            daysToExpiration: daysToTrialExpiration,
            hoursToExpiration: hoursToTrialExpiration,
          }),
  };
};

export default useTrialExtensionBanner;
